import {
  format,
  parse,
  differenceInDays,
  differenceInMonths,
  differenceInWeeks,
  isAfter,
  eachDayOfInterval,
  addDays,
  compareAsc,
  isSameDay,
  isBefore,
  getDaysInMonth,
  lastDayOfMonth,
  parseISO
} from 'date-fns';

import { getBills } from './bills';
import { CONTRACT_TYPE, AVAILABILITY_TYPE } from '@spa/shared/constants/contractType';
import { fromMoneyCoreFormat, toMoneyCoreFormat } from '@spa/shared/utils';

export const DATE_FORMAT_FROM = 'yyyy-MM-dd HH:mm:ss';
export const DATE_FORMAT_PRINT = 'dd MMM yyyy';
export const DATE_FORMAT = 'yyyy-MM-dd';
export const DATE_FORMAT_SHORT_MONTH = 'MMM';
export const DATE_FORMAT_SHORT = 'dd MMM';

export const getContractType = offer => {
  const contract = offer.contract;

  if (contract.type === CONTRACT_TYPE.FIXED) {
    return sortContractOptionsByAvailability(contract.fixed, offer.availabilities.fixed.options);
  }

  if (contract.type === CONTRACT_TYPE.FIXED_UNITARY) {
    return sortContractOptionsByAvailability(contract.fixed_unitary, offer.availabilities.fixed_unitary.options);
  }

  return contract.standard;
};

export const getAvailableFrom = (offer, minimumGap) => {
  const {
    availability_summary_info: availabilitySummaryInfo,
    availabilities: { standard, standard_unitary }
  } = offer;

  if (standard && standard.available_periods) {
    return addDays(toDate(standard.available_periods[0].start_date), minimumGap + 1 || 0);
  }

  if (standard_unitary && standard_unitary.available_periods) {
    return addDays(toDate(standard_unitary.available_periods[0].start_date), minimumGap + 1 || 0);
  }

  if (!availabilitySummaryInfo || !availabilitySummaryInfo.standard_unitary_contract) {
    return null;
  }

  const availableFrom = availabilitySummaryInfo.standard_unitary_contract.available_from;

  return availableFrom ? addDays(toDate(availableFrom), minimumGap + 1 || 0) : new Date();
};

export const getAvailability = (contractType, availability, minimumNights, availableFrom) => {
  if (contractType === CONTRACT_TYPE.FIXED) {
    return availability.fixed;
  }

  if (contractType === CONTRACT_TYPE.FIXED_UNITARY) {
    return availability.fixed_unitary;
  }

  if (availability.standard) {
    return {
      ...transformAvailabilityStandard(availability.standard, minimumNights, availableFrom),
      type: AVAILABILITY_TYPE.STANDARD
    };
  }

  return {
    ...transformAvailabilityStandardUnitary(availability.standard_unitary, minimumNights, availableFrom),
    type: AVAILABILITY_TYPE.STANDARD_UNITARY
  };
};

export const transformContract = (offer, property) => {
  const contract = getContractType(offer);
  const bills = getBills(offer.costs, property);
  const availableFrom = getAvailableFrom(offer);
  const minimumNights = offer.requisites.conditions.minimum_nights;
  const availability = getAvailability(offer.contract.type, offer.availabilities, minimumNights, availableFrom);
  const adminFee = offer.contract.admin_fee.value;
  const securityDeposit = offer.contract.deposit.value;
  const landlordPolicies = offer.contract.landlord_policies.costs.items;
  const penalty = offer.contract.standard.penalty;
  const extraPerGuest = offer.contract.fixed_unitary.extra_per_guest || offer.contract.standard.extra_per_guest;
  const cleaningFrequency = offer.costs.services.cleaning.periodicity;
  const requirements = offer.requisites.requirements;

  return {
    offerId: offer.id,
    offerVersion: offer.version,
    enquire: {
      offerTitle: offer.title
    },
    contract: {
      type: offer.contract.type,
      ...contract,
      price: offer.reference_price,
      availableFrom: availableFrom || availability.availableFromCalendar,
      availability: {
        ...getAvailability(offer.contract.type, offer.availabilities, minimumNights, availableFrom),
        availabilityRules: offer.availabilities.availability_rules
      },
      minimumNights,
      adminFee,
      securityDeposit,
      landlordPolicies,
      penalty,
      extraPerGuest,
      cleaningFrequency,
      requirements
    },
    bills,
    maxGuests: offer.requisites.conditions.max_guests,
    manualFlow: offer.requisites.requirements.manual_flow,
    published: offer.lifecycle.published.published
  };
};

export const toDate = (date, dateFormat = DATE_FORMAT_FROM) => {
  return parse(date, dateFormat, new Date());
};

export const formatDate = (date, toFormat = DATE_FORMAT_PRINT) => {
  return format(date, toFormat);
};

export const getDatesForOption = ({ start_date, end_date }) => {
  const startDate = parse(start_date, DATE_FORMAT_FROM, new Date());
  const endDate = parse(end_date, DATE_FORMAT_FROM, new Date());
  const start = format(startDate, DATE_FORMAT_PRINT);
  const end = format(endDate, DATE_FORMAT_PRINT);

  const diffInMonths = differenceInMonths(endDate, startDate);
  const diffInWeeks = differenceInWeeks(endDate, startDate);
  const diffInDays = differenceInDays(endDate, startDate);

  return { startDate, endDate, start, end, diffInMonths, diffInWeeks, diffInDays };
};

export const getDatesForStandard = moveInOut => {
  if (!moveInOut.start || !moveInOut.end) {
    return { start: '', end: '' };
  }

  const start = formatDate(parseISO(moveInOut.start), DATE_FORMAT);
  const end = formatDate(parseISO(moveInOut.end), DATE_FORMAT);

  return { start, end };
};

export const getPriceForFixedContract = ({ contract_value: price }, diffInDays, isFixedUnitary = false) => {
  if (isFixedUnitary) {
    return price;
  }

  const { currency_code } = price;
  const amount = toMoneyCoreFormat(Math.floor((fromMoneyCoreFormat(price.amount) / diffInDays) * 30));

  return { amount, currency_code };
};

export const getMoveInOutToCheckout = ({ start_date, end_date }) => {
  const startDate = parse(start_date, DATE_FORMAT_FROM, new Date());
  const endDate = parse(end_date, DATE_FORMAT_FROM, new Date());

  return { moveIn: format(startDate, DATE_FORMAT), moveOut: format(endDate, DATE_FORMAT) };
};

export const transformAvailabilityStandardUnitary = ({ blocked_intervals }, minimumNights, availableFrom) => {
  const today = new Date();
  const { blockedDates, blockedPeriods } = blocked_intervals
    ? [...blocked_intervals]
        .sort((firstDate, secondDate) => compareAsc(toDate(firstDate.start_date), toDate(secondDate.start_date)))
        .reduce(
          ({ blockedPeriods, blockedDates }, { start_date, end_date }) => {
            const start = toDate(start_date);
            const end = toDate(end_date);
            const range = { start, end };
            const lastRange = blockedPeriods.length ? blockedPeriods[blockedPeriods.length - 1] : { end: today };
            if (isAfter(lastRange.end, range.end)) {
              return { blockedPeriods, blockedDates };
            }

            const newStart = isAfter(start, today) ? addDays(lastRange.end, minimumNights) : start;
            const modifiedRange = isAfter(newStart, start) ? { start: lastRange.end, end } : range;
            const dates = eachDayOfInterval({ ...modifiedRange });

            return { blockedPeriods: [...blockedPeriods, range], blockedDates: [...blockedDates, ...dates] };
          },
          { blockedPeriods: [], blockedDates: [] }
        )
    : { blockedPeriods: [], blockedDates: [] };

  const availableFromCalendar = blockedDates.reduce(
    (acc, date) => (isSameDay(acc, date) ? addDays(acc, 1) : acc),
    today
  );

  return {
    blocked_intervals,
    blockedPeriods,
    blockedDates,
    availableFromCalendar: isBefore(availableFromCalendar, availableFrom) ? availableFrom : availableFromCalendar
  };
};

export const transformAvailabilityStandard = ({ years }, minimumNights, availableFrom) => {
  const today = new Date();
  const currentYear = parseInt(format(today, 'yyyy'), 10);
  const currentMonth = format(today, 'MMM');

  const blockedDates = years.reduce((acc, { year, months }) => {
    if (parseInt(year, 10) < currentYear) {
      return acc;
    }

    if (year === currentYear) {
      const { blockedYearDates } = Object.keys(months).reduce(
        (currentAcc, key) => {
          if ((key !== currentMonth && currentAcc.lacksCurrent) || months[key] !== 'none') {
            return currentAcc;
          }

          const start = parse(`01-${key}-${year}`, 'dd-MMM-yyyy', new Date());
          const end = lastDayOfMonth(start);

          return {
            blockedYearDates: [...currentAcc.blockedYearDates, ...eachDayOfInterval({ start, end })],
            lacksCurrent: false
          };
        },
        { blockedYearDates: [], lacksCurrent: true }
      );

      return [...acc, ...blockedYearDates];
    }

    const blockedDatesForYear = Object.keys(months).reduce((yearAcc, key) => {
      if (months[key] !== 'none') {
        return yearAcc;
      }

      if (yearAcc.blockedYearDates) {
        return [...yearAcc.blockedYearDates, ...getDaysInMonth(parse(`${key}-${year}`, 'MMM-yyyy'))];
      }

      return [];
    }, []);

    return [...acc, ...blockedDatesForYear];
  }, []);

  const availableFromCalendar = blockedDates.reduce(
    (acc, date) => (isSameDay(acc, date) ? addDays(acc, 1) : acc),
    today
  );

  return {
    blockedPeriods: [],
    blockedDates,
    availableFromCalendar: isBefore(availableFromCalendar, availableFrom) ? availableFrom : availableFromCalendar
  };
};

const sortContractOptionsByAvailability = (contract, availabilityOptions) => {
  return {
    ...contract,
    options: contract.options
      .reduce((acc, option, index) => {
        const isSoldOut = availabilityOptions[index].status === 'none';

        return [...acc, { ...option, isSoldOut }];
      }, [])
      .sort(option => (option.isSoldOut ? 1 : -1))
  };
};
